import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

/**
 * Feature Idea Form
 * Simple form for submitting feature requests
 */
const FeatureIdeaForm = ({ onSubmit, onCancel, isSubmitting }) => {
  const [formData, setFormData] = useState({
    title: '',
    short_description: '',
    description: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title || formData.title.trim().length === 0) {
      newErrors.title = 'Feature title is required';
    } else if (formData.title.length > 255) {
      newErrors.title = 'Title must be less than 255 characters';
    }

    if (!formData.description || formData.description.trim().length === 0) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 50) {
      newErrors.description = 'Description must be at least 50 characters';
    }

    if (formData.short_description && formData.short_description.length > 150) {
      newErrors.short_description = 'Short description must be less than 150 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    onSubmit(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LightbulbIcon color="primary" />
          <Typography variant="body2" color="text.secondary">
            Share your idea for a new feature or improvement!
          </Typography>
        </Box>

        {/* Title */}
        <TextField
          fullWidth
          required
          label="Feature Title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          error={!!errors.title}
          helperText={errors.title || 'What feature would you like to see?'}
          placeholder="e.g., Add dark mode theme"
          inputProps={{ maxLength: 255 }}
          disabled={isSubmitting}
        />

        {/* Short Description */}
        <TextField
          fullWidth
          label="Short Description"
          name="short_description"
          value={formData.short_description}
          onChange={handleChange}
          error={!!errors.short_description}
          helperText={errors.short_description || 'One sentence summary (optional)'}
          placeholder="Quick summary of the feature..."
          inputProps={{ maxLength: 150 }}
          disabled={isSubmitting}
        />

        {/* Description */}
        <TextField
          fullWidth
          required
          multiline
          rows={6}
          label="Detailed Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          error={!!errors.description}
          helperText={errors.description || 'Describe the feature, its benefits, and how you would use it'}
          placeholder="Please tell us:&#10;- What problem does this solve?&#10;- How would you use this feature?&#10;- What would make it great?"
          disabled={isSubmitting}
        />

        {/* Character count */}
        <Typography
          variant="caption"
          color={formData.description.length < 50 ? 'error' : 'text.secondary'}
          sx={{ textAlign: 'right' }}
        >
          {formData.description.length} / 50 minimum
        </Typography>

        {/* Action buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SendIcon />}
            disabled={isSubmitting}
          >
            Submit Feature Idea
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default FeatureIdeaForm;

